import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { 
    Container, 
    Button, 
    Alert,
    Box,
    CircularProgress
} from '@mui/material';
import { applyActionCode } from 'firebase/auth';
import { getAuth } from 'firebase/auth';
import PageHeader from '../../../components/PageHeader';

/**
 * RecoverEmailHandler - Handles email recovery from Firebase email links
 * 
 * This component automatically applies the email recovery action code when mounted.
 * It shows appropriate loading, success, or error states during the process.
 */
export default function RecoverEmailHandler({ 
    oobCode, 
    onSuccess, 
    onError 
}) {
    const [status, setStatus] = useState('recovering'); // 'recovering', 'success', 'error'
    const [errorMessage, setErrorMessage] = useState('');

    const styles = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
    };

    useEffect(() => {
        const handleEmailRecovery = async () => {
            try {
                const auth = getAuth();
                await applyActionCode(auth, oobCode);
                setStatus('success');
            } catch (error) {
                console.error('Email recovery failed:', error);
                
                let msg = 'Failed to recover email. Please try again.';
                switch (error.code) {
                    case 'auth/expired-action-code':
                        msg = 'This recovery link has expired. Please request a new one.';
                        break;
                    case 'auth/invalid-action-code':
                        msg = 'This recovery link is invalid.';
                        break;
                    case 'auth/user-disabled':
                        msg = 'This account has been disabled.';
                        break;
                    case 'auth/user-not-found':
                        msg = 'No account found for this recovery link.';
                        break;
                    default:
                        msg = `Email recovery failed: ${error.message}`;
                }
                setErrorMessage(msg);
                setStatus('error');
            }
        };

        handleEmailRecovery();
    }, [oobCode]);

    useEffect(() => {
        if (status === 'error') {
            onError(errorMessage);
        }
    }, [status, errorMessage, onError]);

    if (status === 'recovering') {
        return (
            <Container sx={styles} maxWidth='md'>
                <PageHeader text='Recovering Your Email...' variant='h4' />
                <CircularProgress sx={{ mt: 3 }} />
            </Container>
        );
    }

    if (status === 'success') {
        return (
            <Container sx={styles} maxWidth='md'>
                <PageHeader text='Email Recovery Confirmed' variant='h4' />
                
                <Alert severity="success" sx={{ mt: 2, width: '100%', maxWidth: 400 }}>
                    Your email has been successfully recovered.
                </Alert>

                <Box sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        onClick={() => onSuccess('Your email has been successfully recovered.')}
                        sx={{ px: 4, py: 1.5 }}
                    >
                        Continue to Login
                    </Button>
                </Box>
            </Container>
        );
    }

    return null; // Error case is handled by redirecting in parent
}

RecoverEmailHandler.propTypes = {
    oobCode: PropTypes.string.isRequired,
    onSuccess: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
};
